import type { AuthTarget } from '../auth/flow';
import { parRequest, tokenRequest } from '../auth/flow';
import { challengeS256, generateVerifier, randomToken } from '../auth/pkce';

/** One transport probe's outcome. */
export interface ProbeResult {
  readonly name: string;
  readonly ok: boolean;
  readonly detail: string;
}

export interface DiagnosticsDeps {
  readonly auth: AuthTarget;
  readonly apiBase: string;
  readonly fetch: (input: string, init?: RequestInit) => Promise<Response>;
  /** The environment's seeded public latency probe (ADR-027 §9) — a share id, not a secret. */
  readonly probeShareId: string;
}

/**
 * The IdP-free transport probe of ADR-029 §7 — the four facts a shell depends on, each
 * observable without a sign-in: a real PAR (a `URLSearchParams` body through the native bridge,
 * no CORS, a JSON answer), a `/token` exchange with a bogus code (the second form post and the
 * error body), an `Authorization` header surviving the WebView's GET proxy (a 401), and a
 * response header read back through it (`X-Resolve-Mode` on the public probe). The dev flavour
 * shows the results on `/?diag=1`; the Maestro flow asserts them.
 */
export async function runDiagnostics(deps: DiagnosticsDeps): Promise<ProbeResult[]> {
  const results: ProbeResult[] = [];
  const probe = async (name: string, run: () => Promise<[boolean, string]>) => {
    try {
      const [ok, detail] = await run();
      results.push({ name, ok, detail });
    } catch (error) {
      results.push({ name, ok: false, detail: error instanceof Error ? error.message : String(error) });
    }
  };

  await probe('PAR (form post through the bridge)', async () => {
    const verifier = generateVerifier();
    const login = { verifier, state: randomToken(), nonce: randomToken() };
    const par = parRequest(deps.auth, await challengeS256(verifier), login);
    const res = await deps.fetch(par.url, { method: 'POST', body: par.body });
    const body = (await res.json()) as { request_uri?: string };
    return [res.status === 201 && typeof body.request_uri === 'string', `HTTP ${res.status}`];
  });

  await probe('token exchange (bogus code → invalid_grant)', async () => {
    const exchange = tokenRequest(deps.auth, 'diagnostics-bogus-code', 'diagnostics-verifier');
    const res = await deps.fetch(exchange.url, { method: 'POST', body: exchange.body });
    const body = (await res.json()) as { error?: string };
    return [res.status === 400 && body.error === 'invalid_grant', `HTTP ${res.status} ${body.error ?? ''}`];
  });

  await probe('Authorization through the GET proxy (garbage bearer → 401)', async () => {
    const res = await deps.fetch(`${deps.apiBase}/plans`, {
      headers: { Authorization: 'Bearer not.a.token' },
    });
    return [res.status === 401, `HTTP ${res.status}`];
  });

  // The share doorway is anonymous, so the resource middleware stamps no X-Access-Level on it
  // (mobile-ci run 28 read it back as absent — from the live API, not the proxy); X-Resolve-Mode
  // is the header every resolved route carries, the one the web smoke gates on.
  await probe('response headers through the GET proxy (X-Resolve-Mode on the public probe)', async () => {
    const res = await deps.fetch(
      `${deps.apiBase}/shares/${encodeURIComponent(deps.probeShareId)}/nodes/latency-probe-16`,
    );
    const mode = res.headers.get('X-Resolve-Mode');
    return [res.status === 200 && mode !== null, `HTTP ${res.status} X-Resolve-Mode=${mode ?? 'absent'}`];
  });

  return results;
}
